import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';
import localizedFormat from 'dayjs/plugin/localizedFormat';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);

export const IST_TZ = 'Asia/Kolkata';

dayjs.tz.setDefault(IST_TZ);

type Input = string | number | Date | Dayjs | null | undefined;

/**
 * Format any value as a date+time string in IST (Asia/Kolkata).
 * Defaults to 12-hour "DD MMM YYYY, hh:mm A" e.g. "06 Jun 2026, 09:12 AM".
 * Returns "—" for null/undefined/invalid input so callers don't need to guard.
 */
export function formatIst(input: Input, pattern = 'DD MMM YYYY, hh:mm A'): string {
  if (input === null || input === undefined || input === '') return '—';
  const d = dayjs(input as any).tz(IST_TZ);
  return d.isValid() ? d.format(pattern) : '—';
}

/** Date only, no time, in IST: "06 Jun 2026". */
export function formatIstDate(input: Input): string {
  return formatIst(input, 'DD MMM YYYY');
}

/** Short time only, in IST 12-hour: "09:12 AM". */
export function formatIstTime(input: Input): string {
  return formatIst(input, 'hh:mm A');
}

/** Long form for audit log / receipts: "06 Jun 2026, 09:12:33 AM IST". */
export function formatIstLong(input: Input): string {
  return formatIst(input, 'DD MMM YYYY, hh:mm:ss A [IST]');
}

/** "5 minutes ago" / "in 2 hours" — uses IST for the reference point too. */
export function formatIstRelative(input: Input): string {
  if (input === null || input === undefined || input === '') return '—';
  const d = dayjs(input as any).tz(IST_TZ);
  return d.isValid() ? d.fromNow() : '—';
}

/** Current moment in IST. Most code should NOT call this — pass times through; this is for "now()" UI labels. */
export function nowIst(): Dayjs {
  return dayjs().tz(IST_TZ);
}

/** Strip a Date to "YYYY-MM-DD" in IST (for date-only fields sent to the API). */
export function toIstDateString(input: Input): string {
  return formatIst(input, 'YYYY-MM-DD');
}
